/**
 * The destinations, along the bottom, on a phone.
 *
 * A top bar is a mouse idea - the top of a phone is the furthest point from
 * your thumb. The five things you actually move between live down here where
 * you can reach them, and everything else is reached from inside a screen.
 *
 * Five, not eight: a row of eight on a 375px screen gives each one 47px, which
 * is below the size a thumb can hit reliably. Games and Badges are one tap
 * from Home, and Staff is not something you do on a phone.
 */

import { useEffect, useState } from 'react';
import { NAV_ICONS } from '@/icons/nav';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

/** True while the window is phone-width. Kept on the body for the CSS too. */
export function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const on = () => {
      setNarrow(mq.matches);
      // The stylesheet keys off this rather than the media query alone, so a
      // rule can say "narrow" without repeating the breakpoint everywhere.
      if (mq.matches) document.body.dataset.narrow = '1';
      else delete document.body.dataset.narrow;
    };
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  return narrow;
}

type Dest = { route: string; label: string; icon: string; badge?: number };

export function TabBar({ route, counts, onNavigate }: {
  route: string;
  counts: { friends: number; unread: number; invites: number };
  onNavigate: (route: string) => void;
}) {
  const dests: Dest[] = [
    { route: 'home', label: 'Home', icon: 'home' },
    { route: 'parties', label: 'Parties', icon: 'parties', badge: counts.invites },
    { route: 'chat', label: 'Chat', icon: 'chat', badge: counts.unread },
    { route: 'friends', label: 'Friends', icon: 'friends', badge: counts.friends },
    // Settings, not alerts. The bell is already in the bar across the top of
    // the screen, and two of the same thing at opposite edges of a phone is
    // one of them wasted - settings had nowhere else to be.
    { route: 'settings', label: 'Settings', icon: 'sliders' }
  ];

  return (
    <nav className="tabbar">
      {dests.map((d) => {
        const NavIcon = NAV_ICONS[d.icon as keyof typeof NAV_ICONS];
        const on = route === d.route;

        return (
          <button
            key={d.route}
            type="button"
            aria-current={on ? 'page' : undefined}
            // Parties always navigates - the app resolves it to whichever
            // party you are in, which is somewhere else than where you are.
            onClick={() => { if (route !== d.route || d.route === 'parties') onNavigate(d.route); }}
            className={cn(
              'group relative flex flex-1 flex-col items-center justify-center gap-0.5 pt-2 pb-1',
              'transition-colors duration-150',
              on ? 'text-accent-hi' : 'text-mute active:text-dim'
            )}
          >
            {/*
              * The current one sits in a lit pill.
              *
              * A phone has no hover and no cursor, so the only way to know
              * which tab you are on is for it to look different - a colour
              * change alone is too quiet at this size.
              */}
            <span
              className={cn(
                'relative grid h-[30px] w-[52px] place-items-center rounded-full transition-[background,box-shadow] duration-200',
                on ? 'bg-accent-soft' : 'bg-transparent'
              )}
              style={on ? { boxShadow: '0 0 12px color-mix(in srgb, var(--accent) 30%, transparent)' } : undefined}
            >
              {NavIcon ? <NavIcon size={21} /> : <Icon name={d.icon} size={21} />}

              {d.badge ? (
                <span className="absolute top-0 right-2 grid h-[16px] min-w-[16px] place-items-center rounded-full bg-accent px-1 text-[9.5px] font-extrabold text-accent-fg ring-2 ring-[var(--bg-side)]">
                  {d.badge > 9 ? '9+' : d.badge}
                </span>
              ) : null}
            </span>

            <span className={cn('text-[10.5px]', on ? 'font-bold' : 'font-semibold')}>{d.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
